'use client'

/**
 * The shared `<Modal>` — an ADAPTER over `@sai/ui`'s `Dialog`
 * (#13202, Stage 4 slice 1 of #13197).
 *
 * `@/components/ui/modal` stays the permanent import path. ~156 consumer
 * files import it and `no-raw-modal-overlay` (#6193) makes it the only door
 * to a modal in the app, so the package swap happens HERE and nowhere else:
 * every exported name, prop and type below is byte-for-byte what it was
 * before, and any mismatch with the package's API is absorbed in this file
 * rather than pushed onto callers.
 *
 * WHAT THE PACKAGE NOW OWNS
 *
 * The dialog root and its context (`Dialog`), the title and description id
 * wiring (`DialogTitle`, `DialogDescription`), the header and footer slots
 * (`DialogHeader`, `DialogFooter`) and the close control (`DialogClose`).
 * That is what retires the raw `<h2>` and the raw `<button>` this file used
 * to hand-roll, and it is why the close control can no longer regress into a
 * form-submitting button by accident.
 *
 * WHY `DialogContent` AND `DialogOverlay` ARE DELIBERATELY NOT USED
 *
 * This is the load-bearing decision in the slice, and it is a DOM-contract
 * constraint rather than a styling preference. Three consumers of this
 * primitive live outside its prop signature:
 *
 *   1. `portalRef` (`src/components/feedback-modal.tsx:562`) sets
 *      `visibility: hidden` on ONE element to hide "the modal, backdrop, and
 *      presentation mode banner" before a screenshot is captured (#3681,
 *      #3771). That requires the backdrop and the panel to share an
 *      ancestor. `sai-ui`'s `DialogContent` renders its own `DialogPortal`
 *      internally with no `container` escape hatch, so its overlay and its
 *      panel land in `document.body` as SIBLINGS — no ancestor exists to
 *      hand to `portalRef`, and the feedback screenshot would silently start
 *      capturing a darkened, blurred page.
 *   2. `data-testid="modal-backdrop"` must be an element this file controls.
 *      `DialogContent` exposes its overlay only through `overlayClassName`,
 *      which can carry neither a testid nor an `onClick` — and that alone is
 *      sufficient, because three suites address the backdrop as a locatable
 *      element with a click handler: `today-brief-widget.test.tsx:224` and
 *      `aor-picker-modal.test.tsx:87` close the modal through it, and
 *      `tests/e2e/a11y/accessibility.spec.ts:1334` scans it. Note the two
 *      unit suites are NOT the same gesture, and only one of them is also
 *      evidence about Radix: `aor-picker-modal.test.tsx:87` is a bare DOM
 *      `.click()`, which dispatches no `pointerdown` and so is invisible to
 *      Radix's outside-dismissal even if the overlay could host a testid;
 *      `today-brief-widget.test.tsx:224` uses `userEvent`, which DOES
 *      dispatch `pointerdown`. Do not cite the second one for the
 *      no-`pointerdown` claim.
 *   3. `DialogOverlay` (the only Dialog part that can host the backdrop
 *      testid and still contain the panel as a child) mounts
 *      `react-remove-scroll` with `shards: [contentRef]`. That blocks wheel
 *      and touch scrolling for any target outside its subtree — and this app
 *      portals dropdown lists to `document.body` from INSIDE modals
 *      (`src/components/ui/searchable-select.tsx`, `select.tsx`,
 *      `dropdown-menu.tsx`, `use-portal-menu.ts`), none of which registers a
 *      lock of its own. Adopting it would make long option lists unscrollable
 *      inside every modal that has one, which no unit test in this repo can
 *      see.
 *
 * So the overlay wrapper, the backdrop and the panel stay this file's own
 * elements, and `tests/unit/components/ui/modal-sai-dialog-adapter-13202
 * .test.tsx` pins each of the three contracts above so a later slice cannot
 * quietly trade one away. Migrating the `modal-overlay` / `modal-backdrop`
 * DOM contract onto `DialogContent` is a separate piece of work — it touches
 * ~15 Playwright specs and `feedback-modal`'s screenshot path — and belongs
 * in its own issue, not in a slice whose contract is that consumers do not
 * change.
 *
 * WHY CADENCE STILL OWNS ESCAPE, SCROLL LOCK AND THE FOCUS TRAP
 *
 * All three are app-wide invariants shared with components that are NOT
 * modals. `useModalEscape` is a single module-level LIFO stack that `Drawer`
 * and the meeting wizard shell also push onto (#7134), so one Escape closes
 * the topmost participant regardless of which component rendered it —
 * `tests/unit/components/drawer.test.tsx` asserts exactly that across the
 * Drawer/Modal boundary. Radix's escape handling is per-layer and cannot see
 * a non-Radix participant, so routing Escape through it would reopen #7134
 * for every drawer-hosted modal. `useBodyScrollLock` is reference-counted
 * across the same set, and `useFocusTrap` is what puts initial focus on the
 * first non-close control rather than on the dialog title (which is where
 * `DialogContent`'s `onOpenAutoFocus` would put it, changing the opening
 * keystroke of ~156 modals).
 *
 * NO THEME AWARENESS, DELIBERATELY. The package's components reference token
 * NAMES, and `src/tokens/` decides what they resolve to in light and dark —
 * so there is nothing here to branch on. Wanting to branch from this file
 * means a token is missing in `src/tokens/`, which is a fix for that layer.
 *
 * @example
 * <Modal isOpen={isOpen} onClose={close} title="Confirm">
 *   <p>Are you sure?</p>
 * </Modal>
 */

import {
  Dialog,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@sai/ui/components'
import { type ReactNode, type Ref, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X } from '@/components/icons'
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock'
import { useFocusTrap } from '@/hooks/use-focus-trap'
import { useKeyboardShortcut } from '@/hooks/use-keyboard-shortcut'
import { useModalEscape } from '@/hooks/use-modal-escape'
import { cn } from '@/lib/utils'

// ============================================================================
// Types
// ============================================================================

type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

/**
 * Which stacking band the overlay renders in (#16841). See `LAYER_CLASSES`.
 */
type ModalLayer = 'default' | 'feedback'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  /** Modal title — renders a header with close button */
  title?: string
  /**
   * Custom sticky header content, rendered in the same pinned header slot as
   * `title` (so it never scrolls with the body). Use when a plain string title
   * isn't enough — e.g. an icon + terminology-driven label. Pair with
   * `ariaLabelledBy` pointing at an id inside the node. Takes precedence over
   * `title`.
   */
  header?: ReactNode
  /** Subtitle shown below the title */
  subtitle?: string
  /** Footer content (e.g. action buttons) */
  footer?: ReactNode
  /** Width variant */
  size?: ModalSize
  /** Prevent closing via backdrop click or Escape */
  preventClose?: boolean
  /** Additional class names for the dialog panel */
  className?: string
  /** Ref to the outermost portal container element */
  portalRef?: Ref<HTMLDivElement>
  /** Custom aria-labelledby ID for modals with custom headers (no title prop) */
  ariaLabelledBy?: string
  /** Minimum height (e.g.'50vh') to prevent resize jitter when filtering */
  minHeight?: string
  /** Called on Cmd+Enter or Ctrl+Enter when provided. Disabled when undefined. */
  onSubmit?: (() => void) | null
  /** Remove default body padding (px-6 py-4) for custom layouts */
  noPadding?: boolean
  /**
   * Stacking band (#16841, #16990). `'default'` (`z-modal`) for every
   * ordinary dialog; `'feedback'` (`z-feedback-modal`) is reserved for
   * `FeedbackModal`, which must open above whatever dialog the viewer is
   * giving feedback about.
   */
  layer?: ModalLayer
}

// ============================================================================
// Constants
// ============================================================================

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

/**
 * The overlay's z-index, per band (#16841, #16990). sai-ui's layer scale
 * (`src/tokens/sai-ui-base.css`) plus Cadence's feedback extension tokens
 * (`src/tokens/index.css`). Bottom to top:
 *
 *   z-nav              40  app chrome (`z-nav-secondary`, 41, the header)
 *   z-modal            50  every ordinary `Modal` and drawer (the default)
 *   z-menu             60  menus, lists and popovers, so one opened inside a
 *                          modal or drawer is above it
 *   z-feedback-picker  70  feedback element picker capture layer
 *   z-feedback-pins    72  feedback pin overlay (`pin-overlay.tsx`)
 *   z-feedback-banner  74  feedback mode banner
 *   z-feedback-popover 76  feedback pin / hidden-threads popover
 *   z-feedback-modal   80  `FeedbackModal` (`layer="feedback"`)
 *   z-toast           100  (sonner's own host sits higher still)
 *
 * The whole panel and its backdrop share this ONE wrapper, so the wrapper
 * carries `z-modal` and the backdrop needs no `z-modal-overlay` of its own:
 * inside the wrapper's stacking context it would only lift the backdrop over
 * the (unpositioned) panel. `tests/unit/tokens/layer-order-16990.test.ts`
 * compiles the stack and asserts the ladder.
 *
 * Before #16841 every modal shared one band and all feedback chrome sat below
 * it, so feedback could never be given about a problem INSIDE a dialog, and
 * the feedback form beat an open dialog only by portal mount order. Before
 * #16990 the bands were Cadence's own numbers (500 / 540…555 / 600), because
 * sai-ui's layer utilities emitted no CSS here.
 */
const LAYER_CLASSES: Record<ModalLayer, string> = {
  default: 'z-modal',
  feedback: 'z-feedback-modal',
}

/**
 * Accessible name for a modal whose caller supplied no naming mechanism at
 * all — no `title`, no `header`, no `ariaLabelledBy`.
 *
 * The first entry in sai-ui's `docs/anti-patterns.mdx` is "Dialog: Missing
 * Title" and its `Dialog` treats the title as required. Cadence's `Modal` has
 * always accepted callers with none, and those modals reached the browser
 * with NO accessible name at all. Supplying the visually-hidden fallback here
 * gives every such call site *a* name without a required prop on 156 files,
 * which is the whole reason the adapter exists.
 *
 * Be precise about what that buys: it fixes the axe RESULT, not the user
 * outcome. "Dialog" tells a screen-reader user nothing, so a call site
 * relying on this is still a call site that needs naming — and the fallback
 * would otherwise erase the evidence, since a nameless dialog is detectable
 * and a generically-named one is not. That is why `Modal` emits a dev-only
 * warning on this path, and why the six call sites reaching it today are
 * listed in PR #13243's `## Follow-ups` rather than absorbed silently.
 *
 * Generic on purpose: this file cannot know a better name than the caller
 * did, and a wrong-but-specific one would be worse than a correct-but-plain
 * one. A caller that wants better passes `title` or `ariaLabelledBy`.
 */
const FALLBACK_DIALOG_TITLE = 'Dialog'

// ============================================================================
// Modal Component
// ============================================================================

export function Modal({
  isOpen,
  onClose,
  children,
  title,
  header,
  subtitle,
  footer,
  size = 'md',
  preventClose = false,
  className,
  portalRef,
  ariaLabelledBy,
  minHeight,
  onSubmit,
  noPadding = false,
  layer = 'default',
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<Element | null>(null)
  // The same predicate `ModalPanel` renders the fallback title from.
  const usesFallbackTitle = modalUsesFallbackTitle(
    title,
    header,
    ariaLabelledBy,
  )

  useBodyScrollLock(isOpen)
  // #7134 — Scope Escape to the TOPMOST modal in a nested-modal stack
  // via a module-level stack of open modals. The previous approach
  // (#6280: per-modal `useKeyboardShortcut` + `stopImmediatePropagation`)
  // assumed the inner modal's listener registered after the outer's
  // and could gate it — but `addEventListener` fires listeners in
  // REGISTRATION order, so the FIRST-opened modal closed instead of
  // the user-visible topmost one (e.g. opening one modal then Feedback,
  // pressing Escape, would close the first modal behind Feedback). The stack
  // approach pops the topmost entry regardless of registration order.
  //
  // The stack is also shared with `Drawer` and the meeting wizard shell,
  // neither of which is a Radix layer — which is why this stays here rather
  // than delegating Escape to the package. See the file header.
  useModalEscape(isOpen && !preventClose, onClose)
  useKeyboardShortcut('Enter', () => onSubmit?.(), {
    meta: true,
    enabled: isOpen && !!onSubmit,
  })
  // Ctrl+Enter is the same save off a Mac, which has no Cmd key (#15788).
  useKeyboardShortcut('Enter', () => onSubmit?.(), {
    ctrl: true,
    enabled: isOpen && !!onSubmit,
  })
  useModalEnterSubmit(dialogRef, isOpen, onSubmit)
  useFocusTrap(dialogRef, isOpen)

  // The fallback title below is a safety net, and a safety net that is also
  // silent is a permanent silencer: before this adapter, a Modal with no
  // naming prop reached the browser with NO accessible name, which
  // `tests/e2e/a11y/` could flag. Naming it "Dialog" makes axe pass while
  // telling a screen-reader user nothing, so the signal that the call site
  // needs a real name has to be re-emitted somewhere. Dev-only console
  // warning, mirroring `src/components/ui/switch.tsx:93-111` — the call
  // sites are consumer files this slice may not edit (see the file header),
  // so the warning is what carries the finding to whoever opens one next.
  // Keyed off `usesFallbackTitle`, NOT off the raw props: all three shapes
  // that reach the fallback deserve the warning, and two of them
  // (`header` with no `ariaLabelledBy`, and `title` alongside `header`) have a
  // truthy naming prop, so a prop-level guard would silence exactly the cases
  // hardest to spot by reading a call site.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return
    if (!isOpen) return
    if (!usesFallbackTitle) return

    console.warn(
      '[Modal] This dialog has no name the adapter can point at, so it is ' +
        'announced as "Dialog". Pass `title`, or `ariaLabelledBy` pointing ' +
        'at a heading already in `children` — note that a `header` node ' +
        'needs `ariaLabelledBy` too, because the adapter cannot know which ' +
        'of its descendants is the title.',
    )
  }, [isOpen, usesFallbackTitle])

  // Save the element that opened the modal so we can
  // restore focus when it closes
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement
    } else if (triggerRef.current) {
      ;(triggerRef.current as HTMLElement).focus?.()
      triggerRef.current = null
    }
  }, [isOpen])

  if (!isOpen) return null
  if (typeof document === 'undefined') return null

  const handleBackdropClick = () => {
    if (!preventClose) {
      onClose()
    }
  }

  /**
   * The dialog root is controlled by `isOpen`, so the only thing that ever
   * asks it to change is `DialogClose`. Routing that through `onOpenChange`
   * instead of a second `onClick={onClose}` is what keeps the close button to
   * exactly one `onClose` call.
   */
  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  return createPortal(
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <div
        ref={portalRef}
        data-testid="modal-overlay"
        className={cn(
          'fixed inset-0 flex items-center justify-center p-4',
          LAYER_CLASSES[layer],
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-surface-scrim backdrop-blur-sm"
          data-testid="modal-backdrop"
          onClick={handleBackdropClick}
          role="button"
          tabIndex={0}
          aria-label="Close dialog"
          onKeyDown={e => {
            if (!preventClose && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault()
              onClose()
            }
          }}
        />

        <ModalPanel
          ref={dialogRef}
          title={title}
          header={header}
          subtitle={subtitle}
          footer={footer}
          size={size}
          preventClose={preventClose}
          className={className}
          ariaLabelledBy={ariaLabelledBy}
          minHeight={minHeight}
          noPadding={noPadding}
        >
          {children}
        </ModalPanel>
      </div>
    </Dialog>,
    document.body,
  )
}

/**
 * Whether the panel names itself with the hidden fallback title — shared by
 * `ModalPanel`'s render and `Modal`'s dev warning so the two cannot disagree.
 */
function modalUsesFallbackTitle(
  title: string | undefined,
  header: ReactNode,
  ariaLabelledBy: string | undefined,
): boolean {
  return !(!header && !!title) && !ariaLabelledBy
}

type ModalPanelProps = Pick<
  ModalProps,
  | 'children'
  | 'title'
  | 'header'
  | 'subtitle'
  | 'footer'
  | 'size'
  | 'preventClose'
  | 'className'
  | 'ariaLabelledBy'
  | 'minHeight'
  | 'noPadding'
> & {
  ref?: Ref<HTMLDivElement>
  /** In place for a preview: a labelled group, not a modal dialog. */
  preview?: boolean
}

/**
 * The panel itself — header with close control, scrolling body, footer —
 * without the portal, backdrop, focus trap or Escape handling. `Modal`
 * renders it over the page; `ModalPreview` renders it in place, so a preview
 * is the real panel rather than a copy of its classes (#14953). Must sit
 * inside a sai-ui `Dialog` root: the title, description and close control
 * read its context.
 */
function ModalPanel({
  ref,
  children,
  title,
  header,
  subtitle,
  footer,
  size = 'md',
  preventClose = false,
  className,
  ariaLabelledBy,
  minHeight,
  noPadding = false,
  preview = false,
}: ModalPanelProps) {
  const titleId = useId()
  const descriptionId = useId()

  // ── Which nodes will actually exist, and therefore what may be pointed at ──
  //
  // `usesFallbackTitle` comes from `modalUsesFallbackTitle`, which `Modal`'s
  // dev warning calls too: the warning has to see the SAME predicate the
  // render uses. Re-deriving it from the raw props is what let the warning
  // and the fallback disagree — `header` without `ariaLabelledBy` takes the fallback
  // and would have returned early from a `title || header || ariaLabelledBy`
  // guard, shipping a generically-named dialog with no warning at all.

  // The header block renders `header ?? <DialogTitle id={titleId}>`, so the
  // string `title` produces an id-bearing node ONLY when `header` is absent —
  // `header` wins the slot. Deriving that rather than testing `title` directly
  // is what keeps `aria-labelledby` from pointing at an id no element has: a
  // caller passing BOTH would otherwise get `aria-labelledby={titleId}` with
  // the `DialogTitle` never rendered, and a name that resolves empty.
  const rendersTitleNode = !header && !!title

  // The adapter supplies its hidden title whenever nothing else in the tree
  // can be pointed at — no id-bearing title node and no caller-supplied
  // `ariaLabelledBy`. That covers three shapes: the bare caller (no naming
  // props at all), `header` with no `ariaLabelledBy` (the caller's node is
  // not addressable from here), and `title` alongside `header`. The fallback
  // is a `sr-only` <span>, not a heading, so adding it next to a consumer
  // `header` cannot change the heading shape consumer specs assert on
  // (`aor-edit-pickers.spec.ts:418` matches `getByRole('heading', { level: 2 })`
  // in Playwright strict mode).
  const usesFallbackTitle = modalUsesFallbackTitle(
    title,
    header,
    ariaLabelledBy,
  )
  const labelledBy =
    rendersTitleNode || usesFallbackTitle ? titleId : ariaLabelledBy

  // `DialogDescription` lives INSIDE the `(title || header)` header block, so
  // a caller passing `subtitle` alone renders no description node — that is
  // pre-existing behaviour and is deliberately not changed here, because
  // making a header row appear where there was none would be a visible layout
  // change at consumer sites. What must track it is `aria-describedby`:
  // pointing it at an id whose element was never rendered yields an EMPTY
  // accessible description, which is worse than none because it reads as
  // wired to a reviewer and says nothing to a user.
  const rendersDescription = !!subtitle && !!(title || header)

  return (
    <div
      ref={ref}
      // A preview is not a dialog: `aria-modal` would also tell
      // `announcement-host.tsx`'s open-modal check that one is up.
      role={preview ? 'group' : 'dialog'}
      aria-modal={preview ? undefined : 'true'}
      aria-labelledby={labelledBy}
      // `DialogDescription` resolves to Radix's `Description`, which
      // renders `<p id={context.descriptionId}>`. That id is consumed in
      // exactly one place in Radix — `DialogContent`'s `aria-describedby`
      // — and this adapter deliberately does not use `DialogContent` (see
      // the file header). So the panel has to point at the subtitle
      // itself, with an id this file owns; without this the subtitle was
      // visible but never announced as the dialog's description.
      //
      // `rendersDescription`, not `subtitle`: the description node lives
      // inside the header block, so a `subtitle` with no `title`/`header`
      // renders nothing to point at. See the derivation above.
      aria-describedby={rendersDescription ? descriptionId : undefined}
      className={cn(
        // sai-ui `DialogContent`'s surface, radius and shadow (#16709). Its
        // `p-6 gap-4` is distributed across the header, body and footer
        // below rather than set here: the body scrolls on its own and
        // `noPadding` callers compose edge-to-edge content inside it, so a
        // panel-level padding would inset both.
        'relative w-full bg-surface-elevated rounded-2xl shadow-xl flex flex-col',
        preview ? 'mx-auto' : 'max-h-[90vh]',
        SIZE_CLASSES[size],
        className,
      )}
      // The one inline style in this file, and the only translation the
      // adapter cannot perform: `minHeight` is a frozen prop carrying an
      // arbitrary runtime CSS length (`'50vh'`, `'420px'`), which no
      // token or utility can express. Kept rather than dropped, because
      // dropping it would be a silent layout change at every call site
      // that passes it.
      style={minHeight ? { minHeight } : undefined}
      onClick={e => e.stopPropagation()}
    >
      {/* `asChild` renders a <span>, not the package's default <h2>: an
          untitled Modal's `children` routinely contain the consumer's own
          heading, and a synthetic sibling heading would change the
          heading count consumer specs match on. The package's id wiring
          is what is being used here, not its typography. */}
      {usesFallbackTitle && (
        <DialogTitle asChild>
          <span className="sr-only" id={titleId}>
            {FALLBACK_DIALOG_TITLE}
          </span>
        </DialogTitle>
      )}

      {/* Header — sticky (never scrolls with the body). `header` node takes
          precedence over the string `title`.

          `DialogHeader` takes the whole header ROW rather than the title
          stack: it is `flex flex-col`, and dropping an arbitrary consumer
          `header` node into a flex column changes how that node's own
          children flow. `flex-row`/`space-y-0` translate the package's
          stack into a row.

          No divider (#16709): sai-ui separates header, body and footer
          with spacing alone. The end padding reserves the corner the
          close control is positioned into, so a long title never runs
          under it.

          TODO(design): sai-ui's `DialogHeader` is `text-center`, and
          `ConfirmationDialog` follows it. The generic Modal keeps
          `text-start` because form modals put start-aligned fields under
          the title, and a centred title over start-aligned labels is the
          open question in
          `vendor/design-knowledge/content/dialog.schema.mjs`. Revisit when
          that is settled upstream. */}
      {(title || header) && (
        <DialogHeader
          className={cn(
            'shrink-0 flex-row items-center space-y-0 px-6 pt-6 pb-2 text-start',
            !preventClose && 'pe-10 pointer-coarse:pe-16',
          )}
        >
          <div className="flex-1 min-w-0">
            {header ?? (
              <DialogTitle
                id={titleId}
                className="text-lg font-semibold text-text-primary"
              >
                {title}
              </DialogTitle>
            )}
            {subtitle && (
              <DialogDescription
                id={descriptionId}
                className="mt-0.5 text-sm text-text-secondary"
              >
                {subtitle}
              </DialogDescription>
            )}
          </div>
          {/* sai-ui's built-in close, reproduced because `DialogContent`
              is not used (file header): `absolute top-3 end-3`, a ghost
              `icon-xs` pill (24px, 16px icon). Positioned against the
              panel, which is `relative`. Touch keeps its 44px floor
              (#6273, #16294). */}
          {!preventClose && (
            <DialogClose
              aria-label="Close dialog"
              className="absolute top-3 end-3 size-6 p-1 pointer-coarse:min-h-11 pointer-coarse:min-w-11 inline-flex items-center justify-center rounded-full text-text-primary hover:bg-action-secondary-hover transition-colors"
            >
              <X className="size-4" />
            </DialogClose>
          )}
        </DialogHeader>
      )}

      {/* Body */}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto',
          // sai-ui's `p-6 gap-4`, split across the three slots: header
          // `pt-6 pb-2` + body `py-2` + footer `pt-2 pb-6` puts 16px between
          // neighbours and 24px at the panel edges. A missing neighbour
          // hands its edge padding to the body.
          !noPadding && 'px-6 py-2',
          !noPadding && !(title || header) && 'pt-6',
          !noPadding && !footer && 'pb-6',
          // Issue #6289 — when noPadding is set, the consumer is composing
          // its own header/content/footer inside `children` and frequently
          // relies on a `flex-1` child to fill the remaining height (e.g.
          // ReviewWizardModal). Without `flex flex-col`
          // here, that `flex-1` has nothing to grow into and the trailing
          // footer floats above the panel's bottom edge.
          noPadding && 'flex flex-col',
        )}
      >
        {children}
      </div>

      {/* Footer — sai-ui's `DialogFooter` as it ships (#16709): centred,
          `gap-2`, and a column that REVERSES below `sm`, so the last child
          (the confirm action, per `dialog-primary-action-goes-last` in
          `vendor/design-knowledge/content/dialog.schema.mjs`) is on top on a
          phone and at the end on a desktop. No `border-t`: spacing separates
          it from the body. Until #16709 this translated the package back
          into Cadence's right-aligned row; that override is gone.

          THE ROW DELIBERATELY DOES NOT WRAP, and #13923 is where that was
          tried and measured. `flex-wrap` looks like the obvious hardening
          — a row that cannot break can overflow, and this panel handles no
          horizontal overflow at all (`overflow-y-auto` is on the body, not
          here and not on the panel). It is not safe to add, because
          flexbox collects items into lines using their UNSHRUNK
          hypothetical main sizes, before any shrinking happens. So a
          footer that fits only BECAUSE its items shrink does not stay one
          line under `flex-wrap` — it breaks.

          Measured on `ConfirmationDialog`'s real footer, whose cancel and
          confirm labels may wrap inside the control (so they shrink
          happily):

            without flex-wrap  1 row,  footer 91px   <- shipped then
            with    flex-wrap  2 rows, footer 119px

          (measured on the right-aligned `border-t` footer #16709 replaced;
          the wrap split is the same at `sm` and up, where the sai footer
          is also a single row)

          at a 100% root, and the same split at 120% and 150%. Nothing
          overflowed in either case. Adding it would therefore have
          restacked ~40 two-button confirmations across the app to buy
          nothing, which is the opposite of the no-op it was argued to be.

          What DID overflow was the Closing nudge's three-`Button` footer
          in a `max-w-sm` panel: `Button` carries `whitespace-nowrap`
          (`src/components/ui/button.tsx`), so each button's automatic
          minimum size is its full single-line label width and shrinking
          bottoms out there. That is fixed at the call site by choosing a
          panel wide enough for its own action count
          (`src/components/meetings/closing-nudge.tsx`), not by letting the
          shared footer reflow every caller. `ConfirmationDialog` lets its
          own `Button` labels wrap for the same reason.

          Residual, stated rather than silently accepted: a `Button`-built
          footer whose labels exceed its panel can still paint outside it
          at `sm` and up. No such call site is known today. The fix when one
          appears is the caller's `size`, or `Button`s that may wrap — not
          this line. Below `sm` the column stacks them, so it cannot.

          `data-testid` because jsdom performs no layout: the class string
          is all the unit tier can pin, and the geometry claim
          (`scrollWidth <= clientWidth`, every button's box inside the
          panel's) needs a browser to address this element. */}
      {footer && (
        <DialogFooter
          data-testid="modal-footer"
          className="shrink-0 px-6 pt-2 pb-6"
        >
          {footer}
        </DialogFooter>
      )}
    </div>
  )
}

/**
 * A `Modal`'s panel rendered in place, for authoring previews: the same
 * header, close control, body and footer people will see, with no portal,
 * backdrop, focus trap or `aria-modal`. Close does nothing — wrap it in an
 * `inert` frame so nothing in it can be focused.
 */
export function ModalPreview(
  props: Omit<ModalPanelProps, 'ref' | 'preview' | 'minHeight'>,
) {
  return (
    <Dialog open onOpenChange={noopOpenChange}>
      <ModalPanel {...props} preview />
    </Dialog>
  )
}

function noopOpenChange(): void {}

/**
 * Plain `ENTER` commits a modal from a single-line text field (#14046).
 *
 * `CMD+ENTER` above has been the only centralised submit since this adapter
 * landed, and it is opt-in per call site: of the ~164 files importing `Modal`,
 * 62 pass `onSubmit` and 22 wrap their children in a `<form>`, where the
 * browser's own implicit submission does it. A modal in neither group — the
 * agenda-template create and rename dialogs were exactly that — has NO
 * keyboard commit at all, and nothing tells its author so.
 *
 * ## Why a panel-scoped listener rather than `useKeyboardShortcut`
 *
 * That hook listens on `document`, and the case most likely to regress here is
 * a keystroke whose `target` is OUTSIDE the panel: `searchable-select.tsx`,
 * `select.tsx`, `dropdown-menu.tsx` and `use-portal-menu.ts` all portal their
 * lists to `document.body` from inside modals, where `ENTER` means "choose
 * this option". Listening on the panel node means those keystrokes are not
 * merely filtered out — they never reach this handler, because the portalled
 * list is not in the panel's subtree at all.
 *
 * ## What it deliberately does not fire on
 *
 * A `<textarea>` or `contentEditable` (`ENTER` is a newline), a `<button>` or
 * `<a>` (`ENTER` is that element's own activation), a checkbox/radio/file
 * input, a disabled or read-only field, an open combobox rendered INSIDE the
 * panel (`aria-expanded="true"` — `ENTER` is the option choice), an IME
 * composition, any chord with a modifier (`CMD+ENTER` is the branch above and
 * must stay a single call), and an input inside a `<form>`, where the
 * browser's implicit submission already commits — firing there would call
 * `onSubmit` twice for the 22 call sites that have one.
 */
function useModalEnterSubmit(
  dialogRef: React.RefObject<HTMLDivElement | null>,
  isOpen: boolean,
  onSubmit: (() => void) | null | undefined,
) {
  // Read through a ref so a caller passing an inline arrow does not detach and
  // re-attach the listener on every render.
  const onSubmitRef = useRef(onSubmit)
  useEffect(() => {
    onSubmitRef.current = onSubmit
  }, [onSubmit])

  const enabled = isOpen && !!onSubmit

  useEffect(() => {
    if (!enabled) return
    const panel = dialogRef.current
    if (!panel) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return
      if (event.defaultPrevented) return
      // `CMD+ENTER` is the other branch; every other chord belongs to
      // whatever the focused control makes of it.
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return
      }
      // Mid-IME `ENTER` commits the composition, not the dialog.
      if (event.isComposing) return
      // AUTO-REPEAT IS NOT A SECOND SUBMIT. A held `ENTER` produces a keydown
      // stream at the OS repeat rate — tens of milliseconds apart — and a
      // caller whose in-flight guard is React state cannot have committed the
      // re-render that sets it before the second one arrives. Same argument as
      // the guards this feature moved off the footer button: a rule enforced
      // only by a later render is a rule the keyboard routes around.
      if (event.repeat) return
      if (!isPlainTextInput(event.target)) return

      event.preventDefault()
      onSubmitRef.current?.()
    }

    panel.addEventListener('keydown', handleKeyDown)
    return () => panel.removeEventListener('keydown', handleKeyDown)
  }, [enabled, dialogRef])
}

/** The one shape `ENTER` may commit from: a live, single-line text field. */
function isPlainTextInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLInputElement)) return false
  if (target.disabled || target.readOnly) return false
  if (!SUBMITTABLE_INPUT_TYPES.has(target.type)) return false
  // An open combobox owns `ENTER` even when its input sits in the panel.
  if (target.getAttribute('aria-expanded') === 'true') return false
  // A form already submits on `ENTER` — see the hook's docblock.
  if (target.closest('form') !== null) return false
  return true
}

/**
 * `input.type` values where `ENTER` means "I am done typing".
 *
 * An allowlist rather than a denylist: a type nobody thought about (a future
 * one, or `file`, where `ENTER` opens the picker) should fall through to its
 * own behaviour rather than silently commit a dialog.
 */
const SUBMITTABLE_INPUT_TYPES = new Set([
  'text',
  'search',
  'email',
  'url',
  'tel',
  'password',
  'number',
  'date',
  'time',
  'datetime-local',
  'month',
  'week',
])
